// (c) chaehongee. Confidential and proprietary.

import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import styles from './Backstage.module.css'
import { useBackstageController } from './hooks/useBackstageController'
import { useMemo, useState, useCallback, useEffect, useRef } from 'react'
import { BackstageHeader } from './BackstageHeader'
import { BackstageDock } from './BackstageDock'
import { BackstageNavigatorProvider, BackstageRoutes, useBackstageNav } from './navigator/Router'
import { ChatPage } from './pages/ChatPage.tsx'
import AIModelsPage from './pages/AIModelsPage.tsx'
import TrainingPage from './pages/TrainingPage.tsx'
import LoraPage from './pages/LoraPage.tsx'
import SavedPage from './pages/SavedPage.tsx'
import PresetPage from './pages/PresetPage.tsx'
import { useAppDispatch, useAppSelector } from '../../store/hooks/hooks'
import { addPlaceholderLora } from '../../store/slices/lorasSlice'
import { useMaskActions } from '../Canvas/MaskContext'
import { NAVIGATION_ITEMS, type BackstageRoute } from './navigator/navigationConfig.ts'
import { useBackstageEvents } from './hooks/useBackstageEvents'
import { clearSessionFresh } from '../../store/slices/sessionTabsSlice'
import { selectActiveSessionId } from '../../store/selectors/sessionSelectors'

export function Backstage(): ReactElement {
    const initialRoute = 'chat'

    return (
        <BackstageNavigatorProvider initial={initialRoute}>
            <BackstageContent />
        </BackstageNavigatorProvider>
    )
}

function BackstageContent(): ReactElement {
    const { t } = useTranslation('canvas')
    const [collapsed, setCollapsed] = useState<boolean>(() => window.localStorage.getItem('knoll.threadCollapsed') === '1')
    const nav = useBackstageNav()
    const dispatch = useAppDispatch()
    const activeSessionId = useAppSelector(selectActiveSessionId)
    const freshSessionIds = useAppSelector((state) => state.sessionTabs.freshSessionIds)
    const maskActions = useMaskActions()
    const lastSessionIdRef = useRef<string | null>(null)

    const {
        session,
        input,
        messages,
        contextUsage,
        bottomRef,
        messagesContainerRef,
        fileInputRef,
        textareaRef,
        canSend,
        setInput,
        handleSend,
        handleSendWithText,
        handleKeyDown,
        handleCompositionStart,
        handleCompositionEnd,
        handlePickFile,
        handleFileChange,
        handleMessagesScroll,
        recommendations,
        isRecommendationsManualRefreshing,
        refreshRecommendations,
    } = useBackstageController()

    const requestCollapse = useCallback((next: boolean) => {
        window.dispatchEvent(new CustomEvent('thread:collapse', { detail: { collapsed: next } }))
        setCollapsed(next)
    }, [])

    // Use extracted event listeners
    useBackstageEvents({
        nav,
        dispatch,
        maskActions,
        setCollapsed,
        requestCollapse,
        refreshRecommendations
    })

    useEffect(() => {
        if (!activeSessionId) return
        if (lastSessionIdRef.current === activeSessionId) return
        lastSessionIdRef.current = activeSessionId
        const isFresh = Boolean(freshSessionIds[activeSessionId])
        if (isFresh) {
            nav.replace('ai-models')
            dispatch(clearSessionFresh(activeSessionId))
        } else {
            nav.replace('chat')
        }
    }, [activeSessionId, freshSessionIds, nav, dispatch])

    const handleTrainingSuccess = useCallback((loraId: string, loraName: string) => {
        dispatch(addPlaceholderLora({ id: loraId, name: loraName }))
        nav.goBack()
    }, [dispatch, nav])

    const isChatView = !collapsed && nav.current === 'chat'
    const rootClass = [
        styles.root,
        collapsed ? styles.rootCollapsed : '',
        !collapsed && !isChatView ? styles.rootNoInput : '',
    ].join(' ').trim()

    const handleNavigate = useCallback((page: BackstageRoute) => {
        nav.replace(page)
    }, [nav])

    const appsWithNav = useMemo(() => NAVIGATION_ITEMS.map((item) => ({
        id: item.id,
        name: t(item.label),
        icon: item.icon,
        action: () => handleNavigate(item.route),
    })), [handleNavigate, t])

    const chatPage = useMemo(() => (
        <ChatPage
            sessionActive={Boolean(session)}
            messages={messages}
            input={input}
            contextUsage={contextUsage}
            bottomRef={bottomRef}
            messagesContainerRef={messagesContainerRef}
            fileInputRef={fileInputRef}
            textareaRef={textareaRef}
            canSend={canSend}
            onPickFile={handlePickFile}
            onFileChange={handleFileChange}
            onMessagesScroll={handleMessagesScroll}
            onInputChange={setInput}
            onKeyDown={handleKeyDown}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            onSend={handleSend}
            onSendPrompt={handleSendWithText}
            recommendations={recommendations}
            onRefreshRecommendations={refreshRecommendations}
            isManualRefreshingRecommendations={isRecommendationsManualRefreshing}
        />
    ), [
        session, messages, input, contextUsage, bottomRef, messagesContainerRef, fileInputRef, textareaRef,
        canSend, handlePickFile, handleFileChange, setInput, handleKeyDown,
        handleCompositionStart, handleCompositionEnd, handleSend, handleSendWithText, handleMessagesScroll,
        recommendations,
        refreshRecommendations,
        isRecommendationsManualRefreshing,
    ])

    return (
        <div className={rootClass}>
            {!collapsed && (
                <BackstageHeader
                    activePage={nav.current}
                    onNavigate={handleNavigate}
                    onCollapse={() => requestCollapse(true)}
                />
            )}
            {collapsed ? (
                <BackstageDock
                    apps={appsWithNav}
                    onSelect={() => requestCollapse(false)}
                />
            ) : (
                <BackstageRoutes
                    routes={[
                        { name: 'chat', element: chatPage },
                        { name: 'ai-models', element: <AIModelsPage /> },
{ name: 'lora', element: <LoraPage /> },
                        { name: 'training', element: <TrainingPage onSuccess={handleTrainingSuccess} /> },
                        { name: 'saved', element: <SavedPage /> },
                        { name: 'preset', element: <PresetPage /> },
                    ]}
                />
            )}
        </div>
    )
}

export default Backstage
